import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { EcrDeploymentsContext } from '../contexts/EcrDeploymentsContext';

interface CreateAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SERVICE_NAMES = [
  'autobulk-api',
  'autobulk-ui',
  'autobulk-database',
  'autobulk-gateway',
  'autobulk-deployment',
  'autobulk-pyplc',
];

const CreateAssignmentModal: React.FC<CreateAssignmentModalProps> = ({ open, onClose, onSuccess }) => {
  const {
    machines,
    ecrDeployments,
    fetchMachines,
    fetchEcrDeployments,
    createMachineDeployment,
    loading,
  } = useContext(EcrDeploymentsContext);

  const [machineSerial, setMachineSerial] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [ecrDeploymentId, setEcrDeploymentId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchMachines();
    }
  }, [open]);

  useEffect(() => {
    if (serviceName) {
      fetchEcrDeployments({ service_name: serviceName });
    }
  }, [serviceName]);

  const handleSubmit = async () => {
    setError('');

    if (!machineSerial || !serviceName || !ecrDeploymentId) {
      setError('All fields are required');
      return;
    }

    try {
      setSubmitting(true);
      await createMachineDeployment({
        machine_serial: machineSerial,
        service_name: serviceName,
        ecr_deployment_id: parseInt(ecrDeploymentId),
        active: true,
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMachineSerial('');
    setServiceName('');
    setEcrDeploymentId('');
    setError('');
    onClose();
  };

  const selectedMachine = machines.find((m) => m.serial_number === machineSerial);
  const filteredDeployments = ecrDeployments.filter((d) => d.service_name === serviceName);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          Create Deployment Assignment
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Assign a deployment version to a machine. If the machine already has an active assignment for this service,
          it will be automatically replaced.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            select
            label="Machine"
            value={machineSerial}
            onChange={(e) => setMachineSerial(e.target.value)}
            margin="normal"
            helperText="Select a machine with Cloudflare channel enabled"
          >
            {machines.map((machine) => (
              <MenuItem key={machine.serial_number} value={machine.serial_number}>
                {machine.serial_number} {machine.description && `- ${machine.description}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Service"
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            margin="normal"
            helperText="Select the service to deploy"
          >
            {SERVICE_NAMES.map((service) => (
              <MenuItem key={service} value={service}>
                {service}
              </MenuItem>
            ))}
          </TextField>

          {serviceName && (
            <TextField
              fullWidth
              select
              label="Deployment Version"
              value={ecrDeploymentId}
              onChange={(e) => setEcrDeploymentId(e.target.value)}
              margin="normal"
              helperText="Select the version to deploy"
              disabled={loading || filteredDeployments.length === 0}
            >
              {filteredDeployments.map((deployment) => (
                <MenuItem key={deployment.ecr_deployment_id} value={deployment.ecr_deployment_id}>
                  {deployment.version} - {deployment.status === 'success' ? '✅' : '❌'} (
                  {new Date(deployment.deployed_at).toLocaleString()})
                </MenuItem>
              ))}
            </TextField>
          )}

          {serviceName && filteredDeployments.length === 0 && !loading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No deployments found for {serviceName}. Please select a different service.
            </Alert>
          )}

          {selectedMachine && serviceName && ecrDeploymentId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Note:
              </Typography>
              <Typography variant="body2">
                Any existing active assignment for <strong>{selectedMachine.serial_number}</strong> with service{' '}
                <strong>{serviceName}</strong> will be automatically deactivated.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!machineSerial || !serviceName || !ecrDeploymentId || submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Creating...' : 'Create Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAssignmentModal;
